import { toast } from "react-toastify"

import { REQ } from "constants/api/requests"
import { IResponse, IResponsePayload } from "constants/interfaces"
import { Permission } from "constants/permissions"

import useApi from "util/api"

/** A role in the system — either built-in (id matches the UserRole enum) or custom (id is a UUID). */
export type RoleRow = {
  id: string
  name: string
  description: string | null
  is_builtin: boolean
  permissions: Permission[]
  user_count: number
  created_at: string
}

export type RoleLookup = {
  id: string
  name: string
  is_builtin: boolean
}

class RoleManagementService {
  public async listRoles(): Promise<RoleRow[] | void> {
    try {
      const { data } = await useApi.get<IResponsePayload<{ roles: RoleRow[] }>>(REQ.ADMIN_DASHBOARD.ROLE_MANAGEMENT.ROLES)
      if (data?.success) return data.response.payload.roles
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to load roles")
    }
  }

  public async listRolesLookup(): Promise<RoleLookup[] | void> {
    try {
      const { data } = await useApi.get<IResponsePayload<{ roles: RoleLookup[] }>>(REQ.ADMIN_DASHBOARD.ROLE_MANAGEMENT.ROLES_LOOKUP)
      if (data?.success) return data.response.payload.roles
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to load roles")
    }
  }

  public async createRole(name: string, description?: string): Promise<RoleRow | void> {
    try {
      const { data } = await useApi.post<IResponsePayload<{ role: RoleRow }>>(REQ.ADMIN_DASHBOARD.ROLE_MANAGEMENT.ROLES, {
        name,
        description
      })
      if (data?.success) return data.response.payload.role
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to create role")
      throw err
    }
  }

  public async deleteRole(id: string): Promise<void> {
    const url = REQ.ADMIN_DASHBOARD.ROLE_MANAGEMENT.ROLE.replace(":id", id)
    try {
      const { data } = await useApi.delete<IResponse<{ ok: boolean }>>(url)
      if (!data?.success) throw new Error("Delete failed")
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to delete role")
      throw err
    }
  }

  public async grant(roleId: string, permission: Permission): Promise<void> {
    const url = REQ.ADMIN_DASHBOARD.ROLE_MANAGEMENT.GRANT.replace(":role", encodeURIComponent(roleId))
    try {
      const { data } = await useApi.post<IResponse<{ ok: boolean }>>(url, { permission })
      if (!data?.success) throw new Error("Grant failed")
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to grant permission")
      throw err
    }
  }

  public async revoke(roleId: string, permission: Permission): Promise<void> {
    const url = REQ.ADMIN_DASHBOARD.ROLE_MANAGEMENT.REVOKE.replace(":role", encodeURIComponent(roleId))
    try {
      const { data } = await useApi.post<IResponse<{ ok: boolean }>>(url, { permission })
      if (!data?.success) throw new Error("Revoke failed")
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to revoke permission")
      throw err
    }
  }
}

export const roleManagementService = new RoleManagementService()
